import {createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState} from 'react';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import {SortEnum, SortField} from '../../../../../__generated__/__types__';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';
import {useProductFilter} from '../hooks/use-product-filter';
import {useSearchParams} from 'next/navigation';
import {ProductQueryParams} from '@/lib/constants';

type ListViewContextProps = ProductListItemsQueryOptions & {
    onFilterChange: (filter: ProductListItemsQueryOptions['filter']) => void;
    onSortChange: (sort: ProductListItemsQueryOptions['sort']) => void;
    onLanguageChange: (language: ProductListItemsQueryOptions['language']) => void;
    onSearchChange: (search: ProductListItemsQueryOptions['search']) => void;
    onPageSizeChange: (pageSize: ProductListItemsQueryOptions['pageSize']) => void;
    onPageChange: (page: ProductListItemsQueryOptions['page']) => void;
    onCurrentPageChange: (currentPage: ProductListItemsQueryOptions['currentPage']) => void;
    onLimitChange: (limit: ProductListItemsQueryOptions['limit']) => void;
    isLoading: boolean;
    onLoadingChange: (isLoading: boolean) => void;
    sortFields: SortField[];
    onSortFieldsChange: (sortFields: SortField[]) => void;
    defaultFilter: ProductListItemsQueryOptions['filter'];
};
const initialListView: ListViewContextProps = {
    isLoading: false,
    onLoadingChange(isLoading: boolean): void {},
    onCurrentPageChange(currentPage: ProductListItemsQueryOptions['currentPage']): void {},
    onFilterChange(filter: ProductListItemsQueryOptions['filter']): void {},
    onLanguageChange(language: ProductListItemsQueryOptions['language']): void {},
    onLimitChange(limit: ProductListItemsQueryOptions['limit']): void {},
    onPageChange(page: ProductListItemsQueryOptions['page']): void {},
    onPageSizeChange(pageSize: ProductListItemsQueryOptions['pageSize']): void {},
    onSearchChange(search: ProductListItemsQueryOptions['search']): void {},
    onSortChange(sort: ProductListItemsQueryOptions['sort']): void {},
    sort: {new_product: SortEnum.Asc}, // Default
    filter: undefined,
    defaultFilter: undefined,
    language: 'vi',
    currentPage: 1,
    sortFields: [],
    limit: PRODUCTS_PER_PAGE,
    pageSize: PRODUCTS_PER_PAGE,
    onSortFieldsChange: () => {},
};
const ProductListViewContext = createContext<ListViewContextProps>(initialListView);

const ProductListViewProvider: FC<DynamicPageServerSideProps & PropsWithChildren<{}>> = ({
    variables,
    children,
}: DynamicPageServerSideProps & PropsWithChildren<{}>) => {
    const query = useSearchParams();

    const {initialFilterOptions, initialPage, initialSearch, initialSort} = useProductFilter();

    const defaultFilter = useMemo((): ProductListItemsQueryOptions['filter'] => {
        if (variables?.categoryId) {
            return {category_id: {eq: `${variables.categoryId}`}};
        }
        if (variables?.brand?.label) {
            return {brand: {eq: variables.brand.label}};
        }
        return {};
    }, [variables?.categoryId, variables?.brand?.label]);

    const [filter, setFilter] = useState<ListViewContextProps['filter']>(() => ({
        ...defaultFilter,
        ...initialFilterOptions,
    }));

    const [sort, setSort] = useState<ListViewContextProps['sort']>(initialSort);
    const [language, setLanguage] = useState<ListViewContextProps['language']>(initialListView.language);
    const [page, setPage] = useState<ListViewContextProps['page']>(initialListView.page);
    const [limit, setLimit] = useState<ListViewContextProps['limit']>(initialListView.limit);
    const [search, setSearch] = useState<ListViewContextProps['search']>(initialSearch);
    const [pageSize, setPageSize] = useState<ListViewContextProps['pageSize']>(initialListView.pageSize);
    const [currentPage, setCurrentPage] = useState<ListViewContextProps['currentPage']>(initialPage);
    const [isLoading, setIsLoading] = useState<ListViewContextProps['isLoading']>(initialListView.isLoading);
    const [sortFields, setSortFields] = useState<SortField[]>([]);

    useEffect(() => {
        const searchQuery = query.get(ProductQueryParams.SEARCH);
        if (searchQuery && searchQuery !== search) {
            setSearch(searchQuery);
        }
    }, [query.toString()]);

    return useMemo(
        () => (
            <ProductListViewContext.Provider
                value={{
                    defaultFilter,
                    filter,
                    onFilterChange: filter => {
                        setFilter(filter);
                    },
                    sort,
                    onSortChange: sort => {
                        setSort(sort);
                    },
                    language,
                    onLanguageChange: language => {
                        setLanguage(language);
                    },
                    page,
                    onPageChange: page => {
                        setPage(page);
                    },
                    limit,
                    onLimitChange: limit => {
                        setLimit(limit);
                    },
                    search,
                    onSearchChange: search => {
                        setSearch(search);
                    },
                    pageSize,
                    onPageSizeChange: pageSize => {
                        setPageSize(pageSize);
                    },
                    currentPage,
                    onCurrentPageChange: currentPage => {
                        setCurrentPage(currentPage);
                    },
                    isLoading,
                    onLoadingChange: isLoading => {
                        setIsLoading(isLoading);
                    },
                    sortFields,
                    onSortFieldsChange: sortFields => {
                        setSortFields(sortFields);
                    },
                }}>
                {children}
            </ProductListViewContext.Provider>
        ),
        [defaultFilter, filter, sort, language, page, limit, search, pageSize, currentPage, isLoading, sortFields],
    );
};

const useProductListView = () => useContext(ProductListViewContext);

export {ProductListViewProvider, useProductListView};
